import * as React from 'react';

import { createColumnHelper } from '@tanstack/react-table';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetPricingPlan } from 'src/features/pricingPlan/api/useGetPricingPlan';
import { useListPricingPlans } from 'src/features/pricingPlan/api/useListPricingPlans';
import CreatePricingPlanVersionFormModal from 'src/features/pricingPlan/components/form/CreatePricingPlanVersionFormModal';
import CellPricingPlanVersionActions from 'src/features/pricingPlan/components/table/CellPricingPlanVersionActions';
import VersionStatusBadge from 'src/features/pricingPlan/components/VersionStatusBadge';
import { PricingPlan } from 'src/features/pricingPlan/utils/types';
import { pricingBillingBadgeOptions, pricingCalculationBadgeOptions } from 'src/features/pricingPlan/utils/constants';
import { pricingPlanMessages } from 'src/features/pricingPlan/utils/messages';
import {
    SectionBlock,
    SectionBlockHead,
    SectionBlockHeader,
    SectionBlockSubtitle,
    SectionBlockTitle,
} from 'src/registry/components/blocks/SectionBlock';
import { CellBadge, CellDatetime } from 'src/registry/components/table';
import { DataTable } from 'src/registry/components/table/DataTable';
import DataTableColumnHeader from 'src/registry/components/table/DataTableColumnHeader';
import { DataTableControls } from 'src/registry/components/table/DataTableControls';
import { DataTablePagination } from 'src/registry/components/table/DataTablePagination';
import useFilters from 'src/registry/hooks/useFilters';
import usePagination from 'src/registry/hooks/usePagination';
import useSearch from 'src/registry/hooks/useSearch';
import useSorting from 'src/registry/hooks/useSorting';
import { Badge } from 'src/registry/ui/Badge';
import { useTitleStore } from 'src/stores/useTitleStore';
import { useParking } from 'src/providers/ParkingProvider';
import { generalMessages } from 'src/utils/messages';
import { PERMISSION } from 'src/features/auth';
import useHasPermissions from 'src/hooks/useHasPermissions';
import BackButton from 'src/features/navigation/components/BackButton';

const sortingOptions = {
    initialSortingState: [{ id: 'name', desc: false }],
};
const searchOptions = { globalFilterKey: 'name' };

const columnHelper = createColumnHelper<PricingPlan>();

function PricingPlanVersions() {
    const { formatMessage, locale } = useIntl();

    const managePricingPlans = useHasPermissions([PERMISSION.MANAGE_PRICING_PLANS]);

    const { planId = '', identity = '' } = useParams();
    const { parkingId } = useParking();

    const { queryFiltersOptions, ...filters } = useFilters();
    const { querySearchOptions, ...search } = useSearch(searchOptions);
    const { querySortingOptions, ...sorting } = useSorting(sortingOptions);
    const { queryPaginationOptions, ...pagination } = usePagination();

    const payload = React.useMemo(
        () => ({
            ...queryFiltersOptions,
            ...querySearchOptions,
            ...querySortingOptions,
            ...queryPaginationOptions,
            identity,
            parkingId,
        }),
        [queryFiltersOptions, querySearchOptions, querySortingOptions, queryPaginationOptions, identity, parkingId],
    );

    const { data: planData } = useGetPricingPlan({ planId, parkingId });

    const planName =
        planData?.translations?.find((plan) => plan.language === locale)?.name ??
        formatMessage(pricingPlanMessages.pricingPlan);

    const { data, isLoading, isError } = useListPricingPlans(payload);

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(pricingPlanMessages.pricingPlanVersionControl));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);

    const columns = [
        columnHelper.accessor('translations', {
            header: DataTableColumnHeader,
            enableSorting: true,
            cell: ({ row }) => {
                const name = row.original.translations.find((translation) => translation.language === locale)?.name;
                const isCurrentVersion = row.original.is_current_version;
                const iconColor = isCurrentVersion ? 'bg-emerald-600' : 'bg-yellow-500';
                const badgeText = formatMessage(
                    isCurrentVersion ? pricingPlanMessages.currentVersion : pricingPlanMessages.scheduledChanges,
                );
                return (
                    <div className="flex flex-col items-start">
                        {name}
                        <Badge>
                            <div className={`size-1.5 rounded-full ${iconColor}`}></div>
                            {badgeText}
                        </Badge>
                    </div>
                );
            },
            meta: {
                title: formatMessage(generalMessages.name),
            },
        }),
        columnHelper.accessor('billing_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                badgeOptions: pricingBillingBadgeOptions,
                title: formatMessage(pricingPlanMessages.billingType),
            },
        }),
        columnHelper.accessor('calculation_type', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellBadge,
            meta: {
                badgeOptions: pricingCalculationBadgeOptions,
                title: formatMessage(pricingPlanMessages.calculationType),
            },
        }),
        columnHelper.accessor('version_number', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            meta: {
                title: formatMessage(pricingPlanMessages.versionNumber),
            },
        }),
        columnHelper.accessor('valid_from', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(pricingPlanMessages.activeFrom),
            },
        }),
        columnHelper.accessor('valid_to', {
            header: DataTableColumnHeader,
            enableSorting: true,
            enableHiding: true,
            cell: CellDatetime,
            meta: {
                title: formatMessage(pricingPlanMessages.activeTo),
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellPricingPlanVersionActions,
        }),
    ];

    return (
        <>
            <BackButton />
            <SectionBlock>
                <SectionBlockHeader className="flex min-h-9 flex-row justify-between">
                    <SectionBlockHead>
                        <SectionBlockTitle className="text-2xl whitespace-nowrap">{planName}</SectionBlockTitle>
                        <SectionBlockSubtitle>
                            <VersionStatusBadge
                                is_current_version={planData?.is_current_version}
                                validFrom={planData?.valid_from}
                            />
                        </SectionBlockSubtitle>
                    </SectionBlockHead>
                </SectionBlockHeader>
            </SectionBlock>
            <DataTable<PricingPlan, any>
                isLoading={isLoading}
                isError={isError}
                columns={columns}
                data={data}
                search={search}
                filters={filters}
                sorting={sorting}
                pagination={pagination}
                semantics={{
                    title: formatMessage(pricingPlanMessages.pricingPlanVersions),
                    entryName: formatMessage(pricingPlanMessages.pricingPlanVersion).toLowerCase(),
                    entryNames: formatMessage(pricingPlanMessages.pricingPlanVersions).toLowerCase(),
                    placeholder: formatMessage(pricingPlanMessages.pricingPlanVersion),
                }}
                components={{
                    controls: DataTableControls,
                    pagination: DataTablePagination,
                    ...(managePricingPlans && { newEntry: CreatePricingPlanVersionFormModal }),
                }}
            />
        </>
    );
}

export default PricingPlanVersions;
